import { useDl } from "../utils/dlToken";
import { useState, useRef, useCallback } from "react";
import axios from "axios";
import { Upload, Stamp, PenLine, Trash2, Download, Loader2, FileUp, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _uid = 0;
const nextId = () => `el-${Date.now()}-${_uid++}`;

export default function AdminSignPdf({ token, uploadUrl, applyUrl, assetUrl: assetUrlProp }) {
  const dl = useDl(token);
  const UPLOAD_URL = uploadUrl || `${API}/admin/sign-pdf/upload`;
  const APPLY_URL = applyUrl || `${API}/admin/sign-pdf/apply`;
  const [job, setJob] = useState(null);
  const [fileName, setFileName] = useState("");
  const [items, setItems] = useState([]); // {id,page,type,x,y,w}
  const [activePage, setActivePage] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const pageRefs = useRef({});
  const drag = useRef(null);

  const assetUrl = (type) => (assetUrlProp ? assetUrlProp(type) : `${API}/admin/owner-asset/${type === "stamp" ? "stamp" : "signature"}?token=${dl}`);
  const aspectOf = (type) => (type === "stamp" ? job?.stamp_aspect : job?.signature_aspect) || 2;

  const reset = () => { setJob(null); setItems([]); setFileName(""); setError(""); setActivePage(0); };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) { setError("Vyberte súbor vo formáte PDF."); return; }
    setUploading(true); setError(""); setItems([]);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(UPLOAD_URL, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setJob(data); setFileName(f.name); setActivePage(0);
    } catch (err) {
      setError(err.response?.data?.detail || "Nahrávanie zlyhalo.");
    } finally { setUploading(false); }
  };

  const addItem = (type) => {
    const w = type === "stamp" ? 0.18 : 0.22;
    setItems((prev) => [...prev, { id: nextId(), page: activePage, type, x: 0.4, y: 0.4, w }]);
  };

  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

  const onPointerDown = useCallback((e, item, mode) => {
    e.preventDefault(); e.stopPropagation();
    const container = pageRefs.current[item.page];
    if (!container) return;
    const rect = container.getBoundingClientRect();
    drag.current = {
      id: item.id, mode, rect,
      startX: e.clientX, startY: e.clientY,
      origX: item.x, origY: item.y, origW: item.w,
      aspect: aspectOf(item.type),
    };
    const move = (ev) => {
      const d = drag.current; if (!d) return;
      const dx = (ev.clientX - d.startX) / d.rect.width;
      const dy = (ev.clientY - d.startY) / d.rect.height;
      setItems((prev) => prev.map((it) => {
        if (it.id !== d.id) return it;
        if (d.mode === "move") {
          const hFrac = (it.w / d.aspect) * (d.rect.width / d.rect.height);
          return { ...it, x: clamp(d.origX + dx, 0, 1 - it.w), y: clamp(d.origY + dy, 0, 1 - hFrac) };
        }
        const maxByBottom = (1 - it.y) * d.aspect * (d.rect.height / d.rect.width);
        const nw = clamp(d.origW + dx, 0.05, Math.min(1 - it.x, maxByBottom));
        return { ...it, w: nw };
      }));
    };
    const up = () => { drag.current = null; window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, [job]);

  const download = async () => {
    if (!items.length) { setError("Pridajte aspoň jednu pečiatku alebo podpis."); return; }
    setDownloading(true); setError("");
    try {
      const placements = items.map(({ page, type, x, y, w }) => ({ page, type, x, y, w }));
      const res = await axios.post(APPLY_URL, { job_id: job.job_id, placements }, {
        headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = (fileName.replace(/\.pdf$/i, "") || "dokument") + "-podpisany.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || "Generovanie PDF zlyhalo. Skúste nahrať PDF znova.");
    } finally { setDownloading(false); }
  };

  return (
    <div data-testid="admin-sign-pdf">
      <div className="mb-6">
        <h2 className="font-display text-2xl mb-1">Podpísať dokument</h2>
        <p className="text-white/40 text-sm">Nahrajte PDF, presuňte pečiatku a podpis na správne miesto a stiahnite hotový dokument. Súbor sa po stiahnutí na serveri neuchováva.</p>
      </div>

      {!job ? (
        <label data-testid="sign-pdf-dropzone"
          className="flex flex-col items-center justify-center gap-3 border border-dashed border-white/20 bg-[#0a0a0a] py-16 cursor-pointer hover:border-[#D4AF37]/50 transition-colors">
          {uploading ? <Loader2 size={32} className="text-[#D4AF37] animate-spin" /> : <FileUp size={32} className="text-[#D4AF37]" />}
          <span className="text-white/70 text-sm">{uploading ? "Načítavam PDF…" : "Kliknite a vyberte PDF dokument"}</span>
          <span className="text-white/30 text-xs">max. 25 MB</span>
          <input type="file" accept="application/pdf,.pdf" onChange={onUpload} className="hidden" data-testid="sign-pdf-input" disabled={uploading} />
        </label>
      ) : (
        <>
          <div className="sticky top-0 z-20 -mx-1 mb-6 flex flex-wrap items-center gap-2 bg-[#050505]/90 backdrop-blur py-3 border-b border-white/10">
            <button onClick={() => addItem("stamp")} data-testid="sign-add-stamp"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 text-sm font-semibold hover:bg-[#F2E3C6] transition-colors">
              <Stamp size={16} /> Pridať pečiatku
            </button>
            <button onClick={() => addItem("signature")} data-testid="sign-add-signature"
              className="inline-flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2 text-sm font-semibold hover:bg-[#D4AF37]/10 transition-colors">
              <PenLine size={16} /> Pridať podpis
            </button>
            <div className="flex-1" />
            <span className="text-white/40 text-xs hidden sm:inline truncate max-w-[180px]">{fileName}</span>
            <button onClick={reset} data-testid="sign-pdf-reset"
              className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-xs text-white/60 hover:text-white hover:border-white/40 transition-colors">
              <RotateCcw size={14} /> Iné PDF
            </button>
            <button onClick={download} disabled={downloading} data-testid="sign-pdf-download"
              className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-500 transition-colors disabled:opacity-50">
              {downloading ? <><Loader2 size={16} className="animate-spin" /> Generujem…</> : <><Download size={16} /> Stiahnuť podpísané</>}
            </button>
          </div>

          {error && <p className="text-red-400 text-sm mb-4" data-testid="sign-pdf-error">{error}</p>}
          <p className="text-white/40 text-xs mb-4">Tip: prvok presuniete ťahaním, veľkosť zmeníte ťahaním za zlatý roh. Nové prvky pribudnú do práve zobrazenej strany.</p>

          <div className="space-y-8">
            {job.pages.map((pg) => (
              <div key={pg.index} data-testid={`sign-page-${pg.index}`}>
                <div className="text-white/40 text-xs mb-2">Strana {pg.index + 1} / {job.pages.length}</div>
                <div
                  ref={(el) => { pageRefs.current[pg.index] = el; }}
                  onPointerDown={() => setActivePage(pg.index)}
                  className={`relative mx-auto shadow-xl select-none ${activePage === pg.index ? "ring-2 ring-[#D4AF37]/60" : "ring-1 ring-white/10"}`}
                  style={{ maxWidth: 780, width: "100%" }}
                >
                  <img src={pg.preview} alt={`Strana ${pg.index + 1}`} className="block w-full h-auto pointer-events-none" draggable={false} />
                  {items.filter((it) => it.page === pg.index).map((it) => (
                    <Overlay key={it.id} item={it} aspect={aspectOf(it.type)} src={assetUrl(it.type)}
                      onPointerDown={onPointerDown} onRemove={() => removeItem(it.id)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Overlay({ item, aspect, src, onPointerDown, onRemove }) {
  // width as % of page; height derived from aspect, expressed via padding-bottom trick on inner img
  return (
    <div
      data-testid={`sign-overlay-${item.type}`}
      onPointerDown={(e) => onPointerDown(e, item, "move")}
      className="absolute group cursor-move border border-dashed border-[#D4AF37]/70 hover:border-[#D4AF37]"
      style={{ left: `${item.x * 100}%`, top: `${item.y * 100}%`, width: `${item.w * 100}%` }}
    >
      <img src={src} alt={item.type} draggable={false}
        className="block w-full h-auto pointer-events-none"
        style={{ aspectRatio: String(aspect) }} />
      <button
        onPointerDown={(e) => { e.stopPropagation(); }}
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        data-testid={`sign-overlay-remove-${item.type}`}
        className="absolute -top-3 -right-3 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <Trash2 size={12} />
      </button>
      <span
        onPointerDown={(e) => onPointerDown(e, item, "resize")}
        data-testid={`sign-overlay-resize-${item.type}`}
        className="absolute -bottom-2 -right-2 w-4 h-4 bg-[#D4AF37] border border-black cursor-se-resize" />
    </div>
  );
}

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
